import { Formik } from "formik";
import { useRef, useState } from "react";
import NumberFormat from "react-number-format";
import * as Yup from "yup";
import Slider from "@mui/material/Slider";
import { MixesValues } from "../@types/common";
import { BREAKPOINTS } from "../constants/common";
import { translation as t } from "../utils/translation";
import { formatNumberWithoutDot } from "../utils/ui";
import { getPayloadMixes, rangeNumber } from "../utils/logic";
import Money from "../assets/icons/money.svg";
import Clock from "../assets/icons/clock.svg";
import { ReactComponent as Pencil } from "../assets/icons/pencil.svg";
import { useWindowSize } from "../hooks/useWindowWidth";
import MixesResults from "./mixesResults";
const MIN_MORTGAGE_SUM = 50000;
const MAX_MORTGAGE_SUM = 10000000;
const MIN_YEARS = 10;
const MAX_YEARS = 30;
const STEP_MORTGAGE_SUM = 50000;
const STEP_YEARS = 5;
function CalculateMixes() {
  const [showResults, setShowResults] = useState(false);
  const resRef = useRef<HTMLDivElement>(null);
  const windowWidth = useWindowSize();
  const { EXTRA_EXTRA_SMALL } = BREAKPOINTS;
  const initialValues: MixesValues = {
    mortgageSum: "50000",
    period: 10,
  };
  const marksData = [
    {
      value: 10,
      label: "10",
    },
    {
      value: 15,
      label: "15",
    },
    {
      value: 20,
      label: "20",
    },
    {
      value: 25,
      label: "25",
    },
    {
      value: 30,
      label: "30",
    },
  ];
  const SignupSchema = Yup.object().shape({
    mortgageSum: Yup.number()
      .min(MIN_MORTGAGE_SUM, "טווח הזנה לשדה 50,000-10,000,000")
      .max(MAX_MORTGAGE_SUM, "טווח הזנה לשדה 50,000-10,000,000")
      .required("שדה חובה"),
    period: Yup.string().required("שדה חובה"),
  });
  const withValueLimit = (inputObj: any) => {
    const { value } = inputObj;
    return value <= MAX_MORTGAGE_SUM;
  };
  const submit = (values: MixesValues) => {
    window.dataLayer.push({
      Category: "Mortgage Calculator",
      Action: "Calculate",
      Label: rangeNumber(Number(values.mortgageSum)),
      event: "auto_event",
      ...getPayloadMixes(values),
    });
    setShowResults(true);
    setTimeout(() => {
      resRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };
  return (
    <div id="calculateMixes" className="w-full">
      <Formik
        initialValues={initialValues}
        validationSchema={SignupSchema}
        onSubmit={() => {}}
      >
        {({ values, errors, touched, handleSubmit, setFieldValue }) => {
          return (
            <form className="text-right row-reverse" onSubmit={handleSubmit}>
              {!showResults ||
              (showResults && windowWidth > EXTRA_EXTRA_SMALL) ? (
                <div className="flex-col items-center">
                  <div className="flex  gap-5 grid xxl:grid-cols-2 xs:grid-rows-2 xxl:grid-rows-1  ">
                    {/* money */}
                    <div className="flex flex-col shadow-m xxl:py-8 xs:py-[20px] items-center justify-center px-[16px]">
                      <div className="flex xs:flex-col xxl:flex-row items-center justify-between  xs:w-[227.06px] xxl:w-64">
                        <img src={Money} className=" flex w-8 h-8" />
                        <p className="bg-bottom mr-[20px] text-darkBlue xs:text-[20px] xxl:text-[30px] xs:font-normal xxl:font-bold">
                          {t.LOAN_AMOUNT_TITLE}
                        </p>
                      </div>
                      <NumberFormat
                        placeholder={t.LOAN_AMOUNT}
                        isNumericString={true}
                        thousandSeparator={true}
                        allowNegative={false}
                        value={values.mortgageSum}
                        isAllowed={withValueLimit}
                        onValueChange={(vals) => {
                          setFieldValue("mortgageSum", vals.value);
                          setShowResults(false);
                        }}
                        onFocus={(event: any) => {
                          event.target.placeholder = `    ${t.ILS_SIGN}`;
                        }}
                        prefix={t.ILS_SIGN}
                        className="border-b border-black  h-14 text-center xs:p-0 xs:my-3 xxl:p-1 xxl:my-4 xs:w-[134px] xxl:w-[256px] xxl:text-[24px] text-grey"
                      />
                      <div className="flex flex-col ">
                        <div className="flex xs:w-[227.06px] xxl:w-64 text-grey">
                          <Slider
                            name="mortgageSum"
                            min={MIN_MORTGAGE_SUM}
                            step={STEP_MORTGAGE_SUM}
                            max={MAX_MORTGAGE_SUM}
                            onChange={(_, value) => {
                              setFieldValue("mortgageSum", value.toString());
                              setShowResults(false);
                            }}
                            value={Number(values.mortgageSum)}
                          />
                          <br />
                        </div>
                        <div className="flex flex-row justify-between text-grey  xs:w-[227.06px] xxl:w-64 text-[14px]">
                          <div>
                            {formatNumberWithoutDot(
                              MAX_MORTGAGE_SUM.toString()
                            )}
                          </div>
                          <div>
                            {formatNumberWithoutDot(
                              MIN_MORTGAGE_SUM.toString()
                            )}
                          </div>
                        </div>
                      </div>
                      {errors.mortgageSum && touched.mortgageSum ? (
                        <div className="text-red">{errors.mortgageSum}</div>
                      ) : null}
                    </div>
                    {/* clock */}
                    <div className="flex flex-col shadow-m xxl:py-8 xs:py-[20px] items-center justify-center px-[16px] ">
                      <div className="flex xs:flex-col xxl:flex-row items-center justify-between">
                        <img src={Clock} className="flex w-8 h-8" />
                        <p className="bg-bottom mr-[20px] text-darkBlue xs:text-[20px] xxl:text-[30px] xs:font-normal xxl:font-bold">
                          {t.PERIOD_TITLE}
                        </p>
                      </div>
                      <div className="p-1 xxl:my-4  xs:my-1 text-grey  xs:text-[16px] xxl:text-[24px]">
                        {values.period} {t.YEARS}
                      </div>
                      <div className="flex flex-col">
                        <div className="flex xs:w-[227.06px] xxl:w-64 text-grey">
                          <Slider
                            name="period"
                            defaultValue={10}
                            onChange={(_, value) => {
                              setFieldValue("period", value);
                              setShowResults(false);
                            }}
                            min={MIN_YEARS}
                            max={MAX_YEARS}
                            step={STEP_YEARS}
                            marks={marksData}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-row justify-evenly p-12">
                    <button
                      className="bg-darkBlue text-white font-bold xxl:text-[20px] xs:text-[18px] py-2 px-10 rounded-m text-center disabled:bg-disabledGrey"                      
                      type="submit"
                      onClick={() => submit(values)}
                      disabled={showResults}
                    >
                      {t.SHOW_RESULTS_BUTTON}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowResults(false)}
                  className="flex justify-around items-center w-full h-[66px] text-blue text-[20px] shadow-m"
                >
                  {`${formatNumberWithoutDot(values.mortgageSum)} ${t.ILS_SIGN}
                  ${t.TO} ${values.period} ${t.YEARS}`}
                  <Pencil
                    className="stroke-blue"
                    title={`${t.ACCESSIBILITY.EDIT}}`}
                  />
                </button>
              )}
              <div className="mixesResults" ref={resRef}>
                {showResults && (
                  <MixesResults
                    loanAmount={Number(values.mortgageSum)}
                    period={Number(values.period)}
                  />
                )}
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}
export default CalculateMixes;